// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Menu,
  Modal,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type {
  DocumentReference,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { QuestionnaireForm, useMedplum, useMedplumProfile } from '@medplum/react';
import {
  IconDotsVertical,
  IconDownload,
  IconEdit,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useMemo, useState } from 'react';
import { TableAnchor } from '../../../components/layout/TableAnchor';
import type { PatientDocument } from '../../../hooks/usePatientDocuments';
import { useRole } from '../../../hooks/useRole';
import { getBinaryDownloadUrl } from '../../../utils/binary';
import { hasPermission } from '../../../utils/permissions';
import {
  createVersionedQuestionnaireDocument,
  getQuestionnaireDocumentSource,
} from '../../../utils/questionnaire-document';
import { withTenantScope } from '../../../utils/tenant';

const PAGE_SIZE = 10;

function formatLastEdited(doc: PatientDocument): { date: string; author: string } {
  const date = doc.date ? dayjs(doc.date).format('MM/DD/YYYY [at] h:mma') : '—';
  return { date, author: doc.authorDisplay ?? '—' };
}

async function openDocument(
  url: string | undefined,
  medplum: ReturnType<typeof useMedplum>,
  contentType?: string,
  data?: string
): Promise<void> {
  if (!url && !data) {
    return;
  }
  try {
    const blob = data
      ? new Blob([Uint8Array.from(atob(data), (character) => character.charCodeAt(0))], {
          type: contentType ?? 'application/octet-stream',
        })
      : await medplum.download(getBinaryDownloadUrl(url) as string);
    const declaredHtml =
      contentType?.toLowerCase().startsWith('text/html') || blob.type.toLowerCase().startsWith('text/html');
    const htmlPrefix = declaredHtml ? '' : (await blob.slice(0, 256).text()).trimStart().slice(0, 64);
    if (declaredHtml || htmlPrefix.startsWith('<!doctype html>') || htmlPrefix.startsWith('<html')) {
      const preview = window.open('about:blank', '_blank');
      if (!preview) {
        return;
      }
      preview.opener = null;
      preview.document.open();
      preview.document.write(await blob.text());
      preview.document.close();
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (err) {
    if (url && !contentType?.toLowerCase().startsWith('text/html')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    showNotification({ color: 'red', message: `Unable to open document: ${normalizeErrorString(err)}` });
  }
}

async function downloadDocument(
  url: string | undefined,
  fileName: string,
  medplum: ReturnType<typeof useMedplum>,
  data?: string,
  contentType?: string
): Promise<void> {
  if (!url && !data) {
    return;
  }
  const blob = data
    ? new Blob([Uint8Array.from(atob(data), (character) => character.charCodeAt(0))], {
        type: contentType ?? 'application/octet-stream',
      })
    : await medplum.download(getBinaryDownloadUrl(url) as string);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

function documentFileName(doc: PatientDocument): string {
  const safeTitle =
    doc.title
      .replaceAll(/[^a-z0-9]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase() || 'document';
  const contentType = doc.documentReference?.content?.[0]?.attachment?.contentType;
  const extension = contentType === 'application/pdf' ? 'pdf' : (contentType?.split('/')[1] ?? 'bin');
  return `${safeTitle}.${extension}`;
}

export interface DocumentsSectionProps {
  patient: Patient;
  documents: PatientDocument[];
  loading: boolean;
  onChanged: (documents: PatientDocument[]) => void;
  onRefresh: () => void;
}

export function DocumentsSection({
  patient,
  documents,
  loading,
  onChanged,
  onRefresh,
}: DocumentsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, isAdmin } = useRole();
  const canEdit = hasPermission({ role, permission: 'edit-patient-profile', isAdmin });

  const [query, setQuery] = useState('');
  const [documentFilter, setDocumentFilter] = useState<'active' | 'all'>('active');
  const [page, setPage] = useState(1);

  const [addOpen, setAddOpen] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addDescription, setAddDescription] = useState('');
  const [addFile, setAddFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const [editDoc, setEditDoc] = useState<PatientDocument | undefined>();
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [answerEditDoc, setAnswerEditDoc] = useState<PatientDocument | undefined>();
  const [answerEditQuestionnaire, setAnswerEditQuestionnaire] = useState<Questionnaire>();
  const [answerEditResponse, setAnswerEditResponse] = useState<QuestionnaireResponse>();
  const [answerEditLoading, setAnswerEditLoading] = useState(false);
  const [answerEditSaving, setAnswerEditSaving] = useState(false);

  const [deleteDoc, setDeleteDoc] = useState<PatientDocument | undefined>();
  const [deleting, setDeleting] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return documents.filter((d) => {
      const isActive = !d.documentReference || d.documentReference.status === 'current';
      const matchesFilter = documentFilter === 'all' || isActive;
      return matchesFilter && `${d.title} ${d.description ?? ''}`.toLowerCase().includes(q);
    });
  }, [documents, documentFilter, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paginated = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const resetAdd = (): void => {
    setAddTitle('');
    setAddDescription('');
    setAddFile(null);
  };

  const handleAdd = async (): Promise<void> => {
    if (!addTitle.trim() || !addFile) {
      showNotification({ color: 'red', message: 'Title and file are required' });
      return;
    }
    setSaving(true);
    try {
      const binary = await medplum.createBinary(addFile, addFile.name, addFile.type);
      const now = new Date().toISOString();
      const docRef: DocumentReference = withTenantScope({
        resourceType: 'DocumentReference',
        status: 'current',
        subject: createReference(patient),
        description: addDescription.trim() || undefined,
        date: now,
        author: profile ? [createReference(profile)] : undefined,
        content: [
          {
            attachment: {
              title: addTitle.trim(),
              url: `Binary/${binary.id}`,
              contentType: addFile.type,
            },
          },
        ],
      });
      const created = await medplum.createResource<DocumentReference>(docRef);
      const newDoc: PatientDocument = {
        id: created.id ?? '',
        title: addTitle.trim(),
        description: addDescription.trim() || undefined,
        url: `Binary/${binary.id}`,
        date: now,
        authorDisplay: created.author?.[0]?.display,
        editable: true,
        documentReference: created,
      };
      onChanged([newDoc, ...documents]);
      onRefresh();
      setAddOpen(false);
      resetAdd();
      showNotification({ color: 'green', message: 'Document added' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (doc: PatientDocument): void => {
    setEditDoc(doc);
    setEditTitle(doc.title);
    setEditDescription(doc.description ?? '');
  };

  const openAnswerEdit = async (doc: PatientDocument): Promise<void> => {
    const source = getQuestionnaireDocumentSource(doc.documentReference as DocumentReference);
    if (!source.responseId || !source.questionnaireName) {
      return;
    }
    setAnswerEditDoc(doc);
    setAnswerEditLoading(true);
    try {
      const [response, questionnaire] = await Promise.all([
        medplum.readResource('QuestionnaireResponse', source.responseId),
        medplum.searchOne('Questionnaire', { name: source.questionnaireName }),
      ]);
      if (!questionnaire) {
        throw new Error('Questionnaire not found');
      }
      setAnswerEditResponse(response);
      setAnswerEditQuestionnaire(questionnaire);
    } catch (err) {
      setAnswerEditDoc(undefined);
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setAnswerEditLoading(false);
    }
  };

  const handleAnswerEditSubmit = async (response: QuestionnaireResponse): Promise<void> => {
    if (!answerEditDoc || !answerEditQuestionnaire) {
      return;
    }
    setAnswerEditSaving(true);
    try {
      const savedResponse = await medplum.createResource<QuestionnaireResponse>(
        withTenantScope({
          ...response,
          status: 'completed',
          subject: createReference(patient),
          questionnaire: `Questionnaire/${answerEditQuestionnaire.id}`,
          authored: new Date().toISOString(),
        })
      );
      await createVersionedQuestionnaireDocument({
        medplum,
        patient,
        practitioner: profile as Practitioner | undefined,
        questionnaire: answerEditQuestionnaire,
        response: savedResponse,
        previous: answerEditDoc.documentReference,
      });
      setAnswerEditDoc(undefined);
      onRefresh();
      showNotification({ color: 'green', message: 'New document version created' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setAnswerEditSaving(false);
    }
  };

  const handleEdit = async (): Promise<void> => {
    if (!editDoc?.documentReference || !editTitle.trim()) {
      showNotification({ color: 'red', message: 'Title is required' });
      return;
    }
    setEditSaving(true);
    try {
      const now = new Date().toISOString();
      const updated: DocumentReference = {
        ...editDoc.documentReference,
        description: editDescription.trim() || undefined,
        date: now,
        author: profile ? [createReference(profile)] : editDoc.documentReference.author,
        content: editDoc.documentReference.content?.map((c, i) =>
          i === 0 ? { ...c, attachment: { ...c.attachment, title: editTitle.trim() } } : c
        ),
      };
      const saved = await medplum.updateResource<DocumentReference>(updated);
      const updatedDoc: PatientDocument = {
        ...editDoc,
        title: editTitle.trim(),
        description: editDescription.trim() || undefined,
        date: now,
        authorDisplay: saved.author?.[0]?.display ?? editDoc.authorDisplay,
        documentReference: saved,
      };
      onChanged(documents.map((d) => (d.id === updatedDoc.id ? updatedDoc : d)));
      onRefresh();
      setEditDoc(undefined);
      showNotification({ color: 'green', message: 'Document updated' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteDoc?.documentReference) {
      return;
    }
    setDeleting(true);
    try {
      await medplum.updateResource<DocumentReference>({ ...deleteDoc.documentReference, status: 'superseded' });
      onChanged(documents.filter((d) => d.id !== deleteDoc.id));
      onRefresh();
      setDeleteDoc(undefined);
      showNotification({ color: 'green', message: 'Document removed' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Documents</Title>
        <Group gap="sm">
          <TextInput
            placeholder="Search…"
            value={query}
            onChange={(e) => {
              setQuery(e.currentTarget.value);
              setPage(1);
            }}
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={200}
            aria-label="Search documents"
          />
          <Select
            aria-label="Document status"
            value={documentFilter}
            onChange={(value) => setDocumentFilter(value === 'all' ? 'all' : 'active')}
            data={[
              { value: 'active', label: 'Active documents' },
              { value: 'all', label: 'All documents' },
            ]}
            size="sm"
            w={170}
            allowDeselect={false}
          />
          <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)} disabled={!canEdit}>
            Add Document
          </Button>
        </Group>
      </Group>

      {loading ? (
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      ) : (
        <Box style={{ overflowX: 'auto' }}>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Last Edited</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginated.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text ta="center" c="dimmed" py="md">
                      No documents on file.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                paginated.map((doc) => {
                  const { date, author } = formatLastEdited(doc);
                  return (
                    <Table.Tr key={doc.id}>
                      <Table.Td>
                        {doc.url ? (
                          <TableAnchor
                            component="button"
                            type="button"
                            size="sm"
                            onClick={() =>
                              openDocument(doc.url, medplum, doc.contentType, doc.data).catch(() => undefined)
                            }
                          >
                            {doc.title}
                          </TableAnchor>
                        ) : (
                          <Text size="sm">{doc.title}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{doc.description ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" component="span">
                          {date}{' '}
                          {author !== '—' && (
                            <>
                              {'by '}
                              <Anchor size="sm" c="polaris" fw="bold">
                                {author}
                              </Anchor>
                            </>
                          )}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Menu position="bottom-end" shadow="md" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" aria-label={`Actions for ${doc.title}`}>
                              <IconDotsVertical size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            {doc.url && (
                              <Menu.Item
                                leftSection={<IconDownload size={16} />}
                                onClick={() =>
                                  downloadDocument(
                                    doc.url,
                                    documentFileName(doc),
                                    medplum,
                                    doc.data,
                                    doc.contentType
                                  ).catch((err) =>
                                    showNotification({
                                      color: 'red',
                                      message: normalizeErrorString(err),
                                      autoClose: false,
                                    })
                                  )
                                }
                              >
                                Download
                              </Menu.Item>
                            )}
                            {doc.sourceResponseId && (
                              <Menu.Item
                                leftSection={<IconEdit size={16} />}
                                onClick={() => openAnswerEdit(doc).catch(() => undefined)}
                                disabled={!canEdit}
                              >
                                Edit answers
                              </Menu.Item>
                            )}
                            {doc.editable && (
                              <>
                                <Menu.Item
                                  leftSection={<IconEdit size={16} />}
                                  onClick={() => openEdit(doc)}
                                  disabled={!canEdit}
                                >
                                  Edit document
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<IconTrash size={16} />}
                                  color="red"
                                  onClick={() => setDeleteDoc(doc)}
                                  disabled={!canEdit}
                                >
                                  Delete
                                </Menu.Item>
                              </>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      {filtered.length > PAGE_SIZE && (
        <Group justify="space-between" mt="sm">
          <Text size="sm" c="dimmed">{`${filtered.length} of ${documents.length}`}</Text>
          <Pagination value={pageSafe} onChange={setPage} total={totalPages} size="sm" />
        </Group>
      )}

      {/* Add Document modal */}
      <Modal
        opened={addOpen}
        onClose={() => {
          setAddOpen(false);
          resetAdd();
        }}
        title="Add Document"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Title"
            placeholder="Document title"
            required
            value={addTitle}
            onChange={(e) => setAddTitle(e.currentTarget.value)}
          />
          <Textarea
            label="Description (optional)"
            placeholder="Brief description of this document…"
            value={addDescription}
            onChange={(e) => setAddDescription(e.currentTarget.value)}
            minRows={3}
          />
          <Box>
            <Text size="sm" fw={500} mb={4}>
              File{' '}
              <Text component="span" c="red">
                *
              </Text>
            </Text>
            <Dropzone
              onDrop={(files) => {
                if (files[0]) {
                  setAddFile(files[0]);
                  if (!addTitle.trim()) {
                    setAddTitle(files[0].name.replace(/\.[^/.]+$/, ''));
                  }
                }
              }}
              maxFiles={1}
              accept={['application/pdf', 'image/*', 'text/plain', 'text/csv']}
            >
              <Stack align="center" gap="xs" py="sm">
                <IconUpload size={24} stroke={1.5} />
                {addFile ? (
                  <Text size="sm" ta="center">
                    {addFile.name}
                  </Text>
                ) : (
                  <Text size="sm" c="dimmed" ta="center">
                    Drop a file here or click to browse
                  </Text>
                )}
              </Stack>
            </Dropzone>
          </Box>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setAddOpen(false);
                resetAdd();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAdd} loading={saving} disabled={!addTitle.trim() || !addFile}>
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={answerEditDoc !== undefined}
        onClose={() => setAnswerEditDoc(undefined)}
        title="Edit questionnaire answers"
        size="lg"
        centered
      >
        {answerEditLoading || !answerEditQuestionnaire || !answerEditResponse ? (
          <Loader />
        ) : (
          <QuestionnaireForm
            questionnaire={answerEditQuestionnaire}
            questionnaireResponse={answerEditResponse}
            onSubmit={handleAnswerEditSubmit}
            submitButtonText={answerEditSaving ? 'Saving…' : 'Save as new document'}
          />
        )}
      </Modal>

      {/* Edit modal */}
      <Modal opened={Boolean(editDoc)} onClose={() => setEditDoc(undefined)} title="Edit Document" centered>
        <Stack gap="md">
          <TextInput label="Title" required value={editTitle} onChange={(e) => setEditTitle(e.currentTarget.value)} />
          <Textarea
            label="Description (optional)"
            value={editDescription}
            onChange={(e) => setEditDescription(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditDoc(undefined)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} loading={editSaving} disabled={!editTitle.trim()}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal opened={Boolean(deleteDoc)} onClose={() => setDeleteDoc(undefined)} title="Remove Document" centered>
        <Stack gap="md">
          <Text>
            Remove <strong>{deleteDoc?.title ?? ''}</strong>? This action can be undone by an administrator.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteDoc(undefined)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleting}>
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
